import { makeAutoObservable } from 'mobx';
import { ReactElement } from 'react';
import DataSourceType from '../DataSourceType';
import stepSelectGoalActiveIcon from '../resources/images/step_select_goal_active.svg';
import {
    DataSettings,
    DataSettingsTypes,
    getDataSettingsConfirmProps,
    isDependentConfigItemsValid,
} from '../wizard/DataSettings';
import DataSource from '../wizard/DataSource';
import Goals from '../wizard/Goals';
import { MappingItemProps } from '../wizard/MappingItem';
import { ConfirmationDialogProps } from '../wizard/StepConfirmation';
import Summary from '../wizard/Summary';
import { ConfigStore } from './ConfigStore';

export interface WizardItem {
    label: string;
    icon: any;
    hasSaveHeaderBtn?: boolean;
    isSaveBtnDisabled?: () => boolean;
    hasSaveForLater?: boolean;
    headerTitle: () => string;
    content: () => ReactElement;
    canAdvance?: (isSaveForLater: boolean, isEdit: boolean) => boolean;
    onNext?: () => void;
    nextButtonLabel?: string;
    getStepConfirmationProps?: (onNext?: () => void) => ConfirmationDialogProps[];
    showSaveConfirmDialog?: (isEdit: boolean) => boolean;
}

export class WizardStore {
    constructor(configStore: ConfigStore) {
        this.setConfigStore(configStore);
        makeAutoObservable(this);
    }

    configStore: ConfigStore;
    setConfigStore = (configStore: ConfigStore) => {
        this.configStore = configStore;
    };

    expandedTab?: string;
    setExpandedTab = (tab: string) => {
        this.expandedTab = tab;
    };

    activeStep?: number = 0;
    setActiveStep = (activeStep: number) => {
        this.activeStep = activeStep;
    };

    loadedConfigMultipleAssignments: { [value: string]: MappingItemProps[] };
    setLoadedConfigMultipleAssignments = (loadedConfigMultipleAssignments: { [value: string]: MappingItemProps[] }) => {
        this.loadedConfigMultipleAssignments = loadedConfigMultipleAssignments;
    };

    openDateSelection?: boolean = false;
    setOpenDateSelection = (openDateSelection: boolean) => {
        this.openDateSelection = openDateSelection;
    };

    mappingTargets?: string[];
    setMappingTargets = (mappingTargets: string[]) => {
        this.mappingTargets = mappingTargets;
    };

    duplicateMappingLabels?: string[] = [];
    setDuplicateMappingLabels = (duplicateMappingLabels: string[]) => {
        this.duplicateMappingLabels = duplicateMappingLabels;
    };

    wizardSteps: WizardItem[];
    setWizardSteps = (wizardSteps: WizardItem[]) => {
        this.wizardSteps = wizardSteps;
    };

    clearedAssignments: MappingItemProps[] = [];
    setClearedAssignments = (clearedAssignments: MappingItemProps[]) => {
        this.clearedAssignments = clearedAssignments;
    };

    clearAssignment = (item: MappingItemProps) => {
        const { clearedAssignments } = this;
        clearedAssignments.push(item);
        this.setClearedAssignments(clearedAssignments);
    };

    cancelAssignments = () => this.setClearedAssignments([]);

    saveAssignments = () =>
        this.clearedAssignments.forEach((item) =>
            this.configStore.setDataSettingsItem(DataSettingsTypes.Mapping, { ...item, value: '' }),
        );

    canAdvanceDataSourceStep = (isSaveForLater: boolean, isEdit: boolean) => {
        const baseCondition = !!(
            this.configStore.source_type &&
            this.configStore.template_config &&
            this.configStore.template_config.environments &&
            this.configStore.template_config.environments[0] &&
            this.configStore.template_config.config_type === this.configStore.source_type.name
        );

        if (baseCondition) {
            if (
                (this.configStore.source_type.name === DataSourceType.svmx ||
                    this.configStore.source_type.name === DataSourceType.sfdc ||
                    this.configStore.source_type.name === DataSourceType.custom) &&
                (this.configStore.sourceCreds || isEdit)
            ) {
                return true;
            }
            if (
                this.configStore.source_type.name === DataSourceType.csv &&
                ((this.configStore.masterFiles && this.configStore.masterFiles.length !== 0) || isEdit)
            ) {
                return true;
            }

            return false;
        }

        return false;
    };

    goalStep: WizardItem = {
        content: () => <Goals configStore={this.configStore} />,
        icon: stepSelectGoalActiveIcon,
        label: 'Goal',
        headerTitle: () => 'Goal',
        canAdvance: (isSaveForLater: boolean, isEdit: boolean) => {
            if (this.configStore.selected_goal) {
                return true;
            }
            return false;
        },
    };

    createDataSourceStep = (nextButtonLabel: String) =>
        ({
            hasSaveForLater: true,
            content: () => <DataSource wizardStore={this} />,
            canAdvance: (isSaveForLater: boolean, isEdit: boolean) =>
                this.canAdvanceDataSourceStep(isSaveForLater, isEdit),
            showSaveConfirmDialog: (isEdit: boolean) => !this.canAdvanceDataSourceStep(false, isEdit),
            onNext: () => {
                const templateConfig = this.configStore.template_config;
                const mappingJson = JSON.parse(this.configStore.template_json);
                const template = {
                    mapping: mappingJson,
                    mapping_raw_json: JSON.stringify(mappingJson, null, '\t'),
                };
                templateConfig.data_processing_config = this.configStore.dataSettings;
                templateConfig.environments[0].template = template;

                this.configStore.setTemplateConfig({ ...templateConfig });
                console.log(this.configStore.partialMode);
                if (this.configStore.partialMode) {
                    this.configStore.setProcessDialogOpen(
                        true,
                        () => this.setActiveStep(this.activeStep + 1),
                        undefined,
                        false,
                        true,
                    );
                } else {
                    this.setActiveStep(this.activeStep + 1);
                }
            },
            nextButtonLabel,
            label: 'Data Source',
            icon: stepSelectGoalActiveIcon,
            headerTitle: () => (this.configStore?.partialMode ? 'Refresh data' : 'Import your data'),
        }) as WizardItem;

    dataSettingsStep: WizardItem = {
        hasSaveHeaderBtn: true,
        isSaveBtnDisabled: () => {
            try {
                JSON.parse(this.configStore.template_json);
            } catch (e) {
                return true;
            }

            return false;
        },
        hasSaveForLater: true,
        content: () => <DataSettings wizardStore={this} />,
        canAdvance: (isSaveForLater: boolean, isEdit: boolean) => {
            if (isSaveForLater || (!isSaveForLater && this.mappingValid())) {
                return true;
            }

            return false;
        },
        onNext: () => {
            if (
                this.configStore.source_type.name === DataSourceType.svmx ||
                this.configStore.source_type.name === DataSourceType.sfdc ||
                this.configStore.source_type.name === DataSourceType.custom
            ) {
                this.setOpenDateSelection(true);
                //this.configStore.setOpenDateSelection(true);
            } else if (this.configStore.source_type.name === DataSourceType.csv) {
                this.configStore.setProcessDialogOpen(true, () => this.setActiveStep(this.activeStep + 1));
            }
        },
        getStepConfirmationProps: (onNext: () => void) =>
            getDataSettingsConfirmProps(
                this.loadedConfigMultipleAssignments,
                this.mappingValid,
                onNext,
                this.getMultipleAssignments,
                this.clearAssignment,
                this.saveAssignments,
                this.cancelAssignments,
            ),
        nextButtonLabel: 'Process Data >>',
        label: 'Mapping',
        icon: stepSelectGoalActiveIcon,
        headerTitle: () => 'Data Settings',
    };

    summaryStep: WizardItem = {
        content: () => <Summary configStore={this.configStore} />,
        onNext: () => {
            this.configStore.resetConfigData();
        },
        nextButtonLabel: 'See all projects',
        label: 'Summary',
        icon: stepSelectGoalActiveIcon,
        headerTitle: () => 'Summary',
    };

    setCreateWizardSteps = () => {
        this.setWizardSteps([this.createDataSourceStep('Configure Data >>'), this.dataSettingsStep, this.summaryStep]);
    };

    setEditDataSettingsSteps = () => {
        this.setWizardSteps([this.dataSettingsStep, this.summaryStep]);
    };

    setCsvPartialSteps = () => {
        this.setWizardSteps([this.createDataSourceStep('Process Data >>'), this.summaryStep]);
    };

    mappingValid = () => {
        if (!this.configStore.dataSettings || this.configStore.dataSettings.config_mapping.mapping.length <= 0) {
            return false;
        }

        const mappingValid = !this.configStore.dataSettings.config_mapping.mapping.some(
            (item) => item.isRequired && item.value === '',
        );

        const labelsMandatoryValid = !this.configStore.dataSettings.config_mapping.mapping.some(
            (item) => item.labelValueDependent && item.label === '' && item.value !== '',
        );

        const mappingDependenciesValid = isDependentConfigItemsValid(
            this.configStore.dataSettings.config_mapping.mapping,
        );

        const allHasMatchedTargets = this.mappingTargets
            ? this.configStore.dataSettings.config_mapping.mapping
                  .filter((item) => item.value !== '')
                  .every((item) => this.mappingTargets.includes(item.value))
            : false;

        return (
            mappingValid &&
            labelsMandatoryValid &&
            mappingDependenciesValid &&
            this.duplicateMappingLabels.length === 0 &&
            allHasMatchedTargets
        );
    };

    getMultipleAssignments = () => {
        const { mapping } = this.configStore.dataSettings.config_mapping;
        if (!mapping) {
            return undefined;
        }
        const allAssignements = mapping.reduce((valuesToFields, configItem) => {
            if (configItem.value !== '') {
                if (!valuesToFields[configItem.value]) {
                    valuesToFields[configItem.value] = [];
                }

                valuesToFields[configItem.value].push(configItem);
            }
            return valuesToFields;
        }, {});

        let res: { [value: string]: MappingItemProps[] } | undefined = undefined;

        for (const value in allAssignements) {
            if (allAssignements[value].length > 1) {
                if (!res) {
                    res = {};
                }
                res[value] = allAssignements[value];
            }
        }

        return res;
    };
}
